/**
 * TASK MOCK DATA
 *
 * Centralized mock data for tasks including list views and records.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';

// ============================================
// DATE HELPERS
// ============================================
const now = new Date();
const daysAgo = (days: number) => {
  const date = new Date(now);
  date.setDate(date.getDate() - days);
  return date;
};
const daysFromNow = (days: number) => {
  const date = new Date(now);
  date.setDate(date.getDate() + days);
  return date;
};
const formatDate = (date: Date) => date.toISOString().split('T')[0];
const formatDateTime = (date: Date) => date.toISOString();

/**
 * DEFAULT TASK LIST VIEWS
 */
export const defaultTaskListViews: ListView[] = [
  {
    id: 'lv_tasks_all',
    name: 'All Tasks',
    apiName: 'all_tasks',
    description: 'All tasks in the system',
    objectApiName: 'Task',
    isDefault: true,
    columns: [
      // Use Gobodo field names with __c suffix for API compatibility
      { fieldApiName: 'Subject__c', fieldLabel: 'Task Title', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'Type__c', fieldLabel: 'Type', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'Status__c', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'Priority__c', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Assigned To', fieldType: 'text', isVisible: true, order: 4 },
      { fieldApiName: 'DueDate__c', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 5 }
    ],
    filters: [],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_tasks_open',
    name: 'Open Tasks',
    apiName: 'open_tasks',
    description: 'All open and in-progress tasks',
    objectApiName: 'Task',
    isDefault: false,
    columns: [
      { fieldApiName: 'Subject__c', fieldLabel: 'Task Title', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'Priority__c', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Assigned To', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'DueDate__c', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 3 },
      { fieldApiName: 'SlaTarget__c', fieldLabel: 'SLA Target', fieldType: 'datetime', isVisible: true, order: 4 }
    ],
    filters: [
      { fieldApiName: 'Status__c', operator: 'notEquals', value: 'Completed' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_tasks_high_priority',
    name: 'High Priority',
    apiName: 'high_priority_tasks',
    description: 'High priority tasks requiring immediate attention',
    objectApiName: 'Task',
    isDefault: false,
    columns: [
      { fieldApiName: 'Subject__c', fieldLabel: 'Task Title', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'Status__c', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Assigned To', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'DueDate__c', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 3 },
      { fieldApiName: 'Origin__c', fieldLabel: 'Source', fieldType: 'text', isVisible: true, order: 4 }
    ],
    filters: [
      { fieldApiName: 'Priority__c', operator: 'equals', value: 'High' }
    ],
    sortBy: {
      fieldApiName: 'DueDate__c',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_tasks_my',
    name: 'My Tasks',
    apiName: 'my_tasks',
    description: 'Tasks assigned to me',
    objectApiName: 'Task',
    isDefault: false,
    columns: [
      { fieldApiName: 'Subject__c', fieldLabel: 'Task Title', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'Type__c', fieldLabel: 'Type', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'Status__c', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'Priority__c', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'DueDate__c', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 4 }
    ],
    filters: [
      { fieldApiName: 'Owner__c', operator: 'equals', value: 'Current User' }
    ],
    createdBy: 'System',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

/**
 * DEFAULT TASK RECORDS
 */
export const defaultTaskRecords: ListViewRecord[] = [
  {
    id: 'task_001',
    title: 'Process credit dispute request',
    description: 'Review and process credit dispute documentation from client',
    type: 'Credit Dispute',
    status: 'In Progress',
    priority: 'High',
    assignee: 'Sarah Johnson',
    owner: 'Sarah Johnson',
    assignedDate: formatDateTime(daysAgo(10)),
    dueDate: formatDate(daysFromNow(10)),
    slaTarget: formatDateTime(daysFromNow(10)),
    source: 'Credit Repair Case',
    clientId: 'client_001',
    clientName: 'John Doe',
    createdBy: 'System',
    createdDate: formatDateTime(daysAgo(10)),
    lastModifiedDate: formatDateTime(daysAgo(5))
  },
  {
    id: 'task_002',
    title: 'Setup credit monitoring',
    description: 'Configure credit monitoring alerts for new client',
    type: 'Credit Monitoring',
    status: 'Open',
    priority: 'Medium',
    assignee: 'Mike Chen',
    owner: 'Mike Chen',
    assignedDate: formatDateTime(daysAgo(8)),
    dueDate: formatDate(daysFromNow(12)),
    slaTarget: formatDateTime(daysFromNow(12)),
    source: 'Credit Development',
    clientId: 'client_002',
    clientName: 'Jane Smith',
    createdBy: 'System',
    createdDate: formatDateTime(daysAgo(8)),
    lastModifiedDate: formatDateTime(daysAgo(8))
  },
  {
    id: 'task_003',
    title: 'Unlock credit with Experian',
    description: 'Complete credit unlock process with Experian',
    type: 'Credit Unlock',
    status: 'Completed',
    priority: 'Low',
    assignee: 'Lisa Brown',
    assignedDate: formatDateTime(daysAgo(15)),
    dueDate: formatDate(daysAgo(5)),
    completedDate: formatDateTime(daysAgo(6)),
    slaTarget: formatDateTime(daysAgo(5)),
    source: 'ID Theft Case',
    clientId: 'client_003',
    clientName: 'Robert Wilson',
    createdBy: 'System',
    createdDate: formatDateTime(daysAgo(15)),
    lastModifiedDate: formatDateTime(daysAgo(6))
  },
  {
    id: 'task_004',
    title: 'Review client documentation',
    description: 'Review and verify all client-submitted documentation',
    type: 'Documentation',
    status: 'Open',
    priority: 'High',
    assignee: 'John Smith',
    assignedDate: formatDateTime(daysAgo(4)),
    dueDate: formatDate(daysFromNow(1)),
    slaTarget: formatDateTime(daysFromNow(1)),
    source: 'Manual Entry',
    clientId: 'client_004',
    clientName: 'Emily Davis',
    createdBy: 'Admin',
    createdDate: formatDateTime(daysAgo(4)),
    lastModifiedDate: formatDateTime(daysAgo(4))
  },
  {
    id: 'task_005',
    title: 'Credit development consultation',
    description: 'Conduct initial credit development consultation call',
    type: 'Consultation',
    status: 'In Progress',
    priority: 'Medium',
    assignee: 'John Smith',
    assignedDate: formatDateTime(daysAgo(6)),
    dueDate: formatDate(daysFromNow(8)),
    slaTarget: formatDateTime(daysFromNow(8)),
    source: 'Credit Development',
    clientId: 'client_005',
    clientName: 'Michael Brown',
    createdBy: 'System',
    createdDate: formatDateTime(daysAgo(6)),
    lastModifiedDate: formatDateTime(daysAgo(3))
  },
  {
    id: 'task_006',
    title: 'Follow up on dispute resolution',
    description: 'Follow up with bureaus on pending dispute resolutions',
    type: 'Follow-up',
    status: 'Completed',
    priority: 'Low',
    assignee: 'John Smith',
    assignedDate: formatDateTime(daysAgo(12)),
    dueDate: formatDate(daysAgo(2)),
    completedDate: formatDateTime(daysAgo(3)),
    slaTarget: formatDateTime(daysAgo(2)),
    source: 'Credit Repair Case',
    clientId: 'client_001',
    clientName: 'John Doe',
    createdBy: 'System',
    createdDate: formatDateTime(daysAgo(12)),
    lastModifiedDate: formatDateTime(daysAgo(3))
  },
  {
    id: 'task_007',
    title: 'Review credit report updates',
    description: 'Review latest credit report updates and notify client',
    type: 'Review',
    status: 'Open',
    priority: 'High',
    assignee: 'John Smith',
    assignedDate: formatDateTime(daysAgo(3)),
    dueDate: formatDate(daysFromNow(11)),
    slaTarget: formatDateTime(daysFromNow(11)),
    source: 'Credit Monitoring',
    clientId: 'client_002',
    clientName: 'Jane Smith',
    createdBy: 'System',
    createdDate: formatDateTime(daysAgo(3)),
    lastModifiedDate: formatDateTime(daysAgo(3))
  }
];

/**
 * MIGRATION: Fix field name mismatches
 *
 * This migration runs automatically to fix any existing records that use
 * old field names:
 * - 'task' -> 'title'
 * - 'assignedTo' -> 'assignee'
 */
function migrateTaskFieldNames(records: ListViewRecord[]): ListViewRecord[] {
  return records.map(record => {
    let migratedRecord = { ...record };

    // Migrate 'task' -> 'title'
    if ('task' in migratedRecord && !migratedRecord.title) {
      const { task, ...rest } = migratedRecord as any;
      migratedRecord = {
        ...rest,
        title: task
      };
    }

    // Migrate 'assignedTo' -> 'assignee'
    if ('assignedTo' in migratedRecord && !migratedRecord.assignee) {
      const { assignedTo, ...rest } = migratedRecord as any;
      migratedRecord = {
        ...rest,
        assignee: assignedTo
      };
    }

    return migratedRecord;
  });
}

/**
 * PERSISTED STORAGE
 */
export const persistedTaskListViews = createPersistedMockData(
  'crm_task_list_views',
  defaultTaskListViews
);

// Create persisted storage with automatic migration
const taskRecordsStorage = createPersistedMockData(
  'crm_task_records',
  defaultTaskRecords
);

// Wrap get() to apply migration
const originalGet = taskRecordsStorage.get.bind(taskRecordsStorage);
taskRecordsStorage.get = () => {
  const records = originalGet();
  const migratedRecords = migrateTaskFieldNames(records);

  // If migration changed anything, save the migrated data
  if (JSON.stringify(records) !== JSON.stringify(migratedRecords)) {
    taskRecordsStorage.set(migratedRecords);
  }

  return migratedRecords;
};

export const persistedTaskRecords = taskRecordsStorage;

/**
 * BACKWARD COMPATIBILITY
 */
export const mockTaskListViews = defaultTaskListViews;
export const mockTaskRecords = defaultTaskRecords;
